import {
  assembleModelContext,
  type CompactionRecord,
  type CompactionStore,
} from '../compaction';
import { UserError } from '../errors';
import { getCompactionRecordFromRunItem, type RunItem } from '../items';
import logger from '../logger';
import { type Session, type SessionInputCallback } from '../memory/session';
import type { RunResult, StreamedRunResult } from '../result';
import type { RunState } from '../run-state';
import type { AgentInputItem } from '../types';
import { encodeUint8ArrayToBase64 } from '../utils/base64';
import { toUint8ArrayFromBinary } from '../utils/binary';
import {
  collectAgentInputItemIds,
  ensureAgentInputItemIds,
} from './item-identity';
import {
  buildAgentInputPool,
  dropOrphanToolCalls,
  extractOutputItemsFromRunItems,
  extractPersistableOutputItemsFromRunItems,
  getAgentInputItemKey,
  type ReasoningItemIdPolicy,
  removeAgentInputFromPool,
  toAgentInputList,
} from './items';
import {
  getAssistantTurnStatus,
  recordSessionFullTurn,
  recordSessionInputTurn,
  type SessionTurnContext,
} from './session-graph';

export type SessionPersistenceTracker = {
  setPreparedItems: (items?: AgentInputItem[]) => void;
  recordTurnItems: (
    sourceItems: (AgentInputItem | undefined)[],
    filteredItems?: AgentInputItem[],
  ) => void;
  getItemsForPersistence: () => AgentInputItem[] | undefined;
  buildPersistInputOnce: (
    serverManagesConversation: boolean,
  ) => (() => Promise<void>) | undefined;
  setTurnContext: (turnContext?: SessionTurnContext) => void;
  getTurnContext: () => SessionTurnContext | undefined;
};

export function createSessionPersistenceTracker(options: {
  session?: Session;
  hasCallModelInputFilter: boolean;
  persistInput?: typeof saveStreamInputToSession;
  resumingFromState?: boolean;
}): SessionPersistenceTracker | undefined {
  const { session } = options;
  if (!session) {
    return undefined;
  }

  class SessionPersistenceTrackerImpl implements SessionPersistenceTracker {
    private readonly session?: Session;
    private readonly hasCallModelInputFilter: boolean;
    private readonly persistInput?: typeof saveStreamInputToSession;
    private originalSnapshot: AgentInputItem[] | undefined;
    private filteredSnapshot: AgentInputItem[] | undefined;
    private pendingWriteCounts: Map<string, number> | undefined;
    private persistedInput = false;
    private turnContext: SessionTurnContext | undefined;

    constructor() {
      this.session = options.session;
      this.hasCallModelInputFilter = options.hasCallModelInputFilter;
      this.persistInput = options.persistInput;
      this.originalSnapshot = options.resumingFromState ? [] : undefined;
      this.filteredSnapshot = undefined;
      this.pendingWriteCounts = options.resumingFromState
        ? new Map()
        : undefined;
    }

    setPreparedItems = (items?: AgentInputItem[]) => {
      const sessionItems = items ?? [];
      this.originalSnapshot = sessionItems.map((item) => structuredClone(item));
      this.pendingWriteCounts = new Map();
      for (const item of sessionItems) {
        const key = getAgentInputItemKey(item);
        this.pendingWriteCounts.set(
          key,
          (this.pendingWriteCounts.get(key) ?? 0) + 1,
        );
      }
    };

    recordTurnItems = (
      sourceItems: (AgentInputItem | undefined)[],
      filteredItems?: AgentInputItem[],
    ) => {
      const pendingCounts = this.pendingWriteCounts;
      if (filteredItems !== undefined) {
        if (!pendingCounts) {
          this.filteredSnapshot = cloneItems(filteredItems);
          return;
        }
        const nextSnapshot = collectPersistableFilteredItems({
          pendingCounts,
          sourceItems,
          filteredItems,
          existingSnapshot: this.filteredSnapshot,
        });
        if (nextSnapshot !== undefined) {
          this.filteredSnapshot = nextSnapshot;
        }
        return;
      }

      this.filteredSnapshot = buildSnapshotForUnfilteredItems({
        pendingCounts,
        sourceItems,
        existingSnapshot: this.filteredSnapshot,
      });
    };

    getItemsForPersistence = () => {
      if (this.filteredSnapshot !== undefined) {
        return this.filteredSnapshot;
      }
      if (this.hasCallModelInputFilter) {
        return undefined;
      }
      return this.originalSnapshot;
    };

    setTurnContext = (turnContext?: SessionTurnContext) => {
      this.turnContext = turnContext;
    };

    getTurnContext = () => this.turnContext;

    buildPersistInputOnce = (serverManagesConversation: boolean) => {
      if (!this.session || serverManagesConversation) {
        return undefined;
      }
      const persistInput = this.persistInput ?? saveStreamInputToSession;
      return async () => {
        if (this.persistedInput) {
          return;
        }
        const itemsToPersist = this.getItemsForPersistence();
        if (!itemsToPersist || itemsToPersist.length === 0) {
          return;
        }
        this.persistedInput = true;
        await persistInput(this.session, itemsToPersist, {
          turnContext: this.turnContext,
        });
      };
    };
  }

  return new SessionPersistenceTrackerImpl();
}

function normalizeItemsForSessionPersistence(
  items: AgentInputItem[],
): AgentInputItem[] {
  return items.map((item) => {
    const withoutTransientIds = stripTransientCallIds(item);
    return sanitizeValueForSession(
      summarizeViewImageToolResultForSession(withoutTransientIds),
    ) as AgentInputItem;
  });
}

type SessionBinaryContext = {
  mediaType?: string;
};

function sanitizeValueForSession(
  value: AgentInputItem,
  context?: SessionBinaryContext,
): AgentInputItem;
function sanitizeValueForSession(
  value: unknown,
  context?: SessionBinaryContext,
): unknown;
function sanitizeValueForSession(
  value: unknown,
  context: SessionBinaryContext = {},
): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  const binary = toUint8ArrayFromBinary(value);
  if (binary) {
    return toDataUrlFromBytes(binary, context.mediaType);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeValueForSession(entry, context));
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const record = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};

  const mediaType =
    typeof record.mediaType === 'string' && record.mediaType.length > 0
      ? (record.mediaType as string)
      : context.mediaType;

  for (const [key, entry] of Object.entries(record)) {
    const nextContext =
      key === 'data' || key === 'fileData' ? { mediaType } : context;
    result[key] = sanitizeValueForSession(entry, nextContext);
  }

  return result;
}

function toDataUrlFromBytes(bytes: Uint8Array, mediaType?: string): string {
  const base64 = encodeUint8ArrayToBase64(bytes);
  const type =
    mediaType && !mediaType.startsWith('data:') ? mediaType : 'text/plain';
  return `data:${type};base64,${base64}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function summarizeViewImageToolResultForSession(value: unknown): unknown {
  if (!isPlainObject(value) || value.type !== 'function_call_result') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const metadata = getViewImageMetadataFromToolResult(record);
  if (!metadata) {
    return value;
  }

  const { providerData: _providerData, ...rest } = record;
  const display = normalizeViewImageDisplay(metadata);
  return {
    ...rest,
    output: renderViewImageSessionSummary(display),
    display,
  };
}

function getViewImageMetadataFromToolResult(
  item: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const display = isViewImageDisplay(item.display) ? item.display : undefined;
  if (display) {
    return display;
  }

  const output = item.output;
  if (!Array.isArray(output)) {
    return undefined;
  }
  for (const part of output) {
    if (!isPlainObject(part) || part.type !== 'input_image') {
      continue;
    }
    const providerData = part.providerData;
    if (isViewImageDisplay(providerData)) {
      return providerData;
    }
  }
  return undefined;
}

function isViewImageDisplay(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false;
  }
  return (
    value.kind === 'view_image' &&
    typeof value.path === 'string' &&
    typeof value.mimeType === 'string'
  );
}

function renderViewImageSessionSummary(
  metadata: Record<string, unknown>,
): string {
  const path = String(metadata.path);
  const mimeType = String(metadata.mimeType);
  const width = typeof metadata.width === 'number' ? metadata.width : undefined;
  const height =
    typeof metadata.height === 'number' ? metadata.height : undefined;
  const byteSize =
    typeof metadata.byteSize === 'number' ? metadata.byteSize : undefined;
  const dimensions = width && height ? `, ${width}x${height}` : '';
  const size = byteSize !== undefined ? `, ${formatByteSize(byteSize)}` : '';
  return `已读取图片：${path} (${mimeType}${dimensions}${size})。如后续仍需查看图片，请再次调用 view_image。`;
}

function normalizeViewImageDisplay(
  metadata: Record<string, unknown>,
): Record<string, unknown> {
  const display: Record<string, unknown> = {
    kind: 'view_image',
    path: metadata.path,
    mimeType: metadata.mimeType,
  };
  for (const key of [
    'byteSize',
    'width',
    'height',
    'pixelCount',
    'estimatedImageTokens',
  ]) {
    const value = metadata[key];
    if (typeof value === 'number') {
      display[key] = value;
    }
  }
  return display;
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes}B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function cloneItems(items: AgentInputItem[]): AgentInputItem[] {
  return items.map((item) => structuredClone(item));
}

function collectPersistableFilteredItems(options: {
  pendingCounts: Map<string, number>;
  sourceItems: (AgentInputItem | undefined)[];
  filteredItems: AgentInputItem[];
  existingSnapshot: AgentInputItem[] | undefined;
}): AgentInputItem[] | undefined {
  const { pendingCounts, sourceItems, filteredItems, existingSnapshot } =
    options;
  const persistableItems: AgentInputItem[] = [];
  const sourceOccurrenceCounts = buildSourceOccurrenceCounts(sourceItems);
  const consumeAnyPendingWriteSlot = () => {
    for (const [key, remaining] of pendingCounts) {
      if (remaining > 0) {
        pendingCounts.set(key, remaining - 1);
        return true;
      }
    }
    return false;
  };

  for (let i = 0; i < filteredItems.length; i++) {
    const filteredItem = filteredItems[i];
    if (!filteredItem) {
      continue;
    }
    let allocated = false;
    const source = sourceItems[i];
    if (source && typeof source === 'object') {
      const pendingOccurrences = (sourceOccurrenceCounts.get(source) ?? 0) - 1;
      sourceOccurrenceCounts.set(source, pendingOccurrences);
      if (pendingOccurrences > 0) {
        continue;
      }
      const sourceKey = getAgentInputItemKey(source);
      const remaining = pendingCounts.get(sourceKey) ?? 0;
      if (remaining > 0) {
        pendingCounts.set(sourceKey, remaining - 1);
        persistableItems.push(structuredClone(filteredItem));
        allocated = true;
        continue;
      }
    }
    const filteredKey = getAgentInputItemKey(filteredItem);
    const filteredRemaining = pendingCounts.get(filteredKey) ?? 0;
    if (filteredRemaining > 0) {
      pendingCounts.set(filteredKey, filteredRemaining - 1);
      persistableItems.push(structuredClone(filteredItem));
      allocated = true;
      continue;
    }
    if (!source && consumeAnyPendingWriteSlot()) {
      persistableItems.push(structuredClone(filteredItem));
      allocated = true;
    }
    if (!allocated && !source && existingSnapshot === undefined) {
      persistableItems.push(structuredClone(filteredItem));
    }
  }
  if (persistableItems.length > 0 || existingSnapshot === undefined) {
    return persistableItems;
  }
  return existingSnapshot;
}

function buildSnapshotForUnfilteredItems(options: {
  pendingCounts: Map<string, number> | undefined;
  sourceItems: (AgentInputItem | undefined)[];
  existingSnapshot: AgentInputItem[] | undefined;
}): AgentInputItem[] {
  const { pendingCounts, sourceItems, existingSnapshot } = options;
  if (!pendingCounts) {
    const filtered = sourceItems
      .filter((item): item is AgentInputItem => Boolean(item))
      .map((item) => structuredClone(item));
    return filtered.length > 0
      ? filtered
      : existingSnapshot === undefined
        ? []
        : existingSnapshot;
  }

  const filtered: AgentInputItem[] = [];
  for (const item of sourceItems) {
    if (!item) {
      continue;
    }
    const key = getAgentInputItemKey(item);
    const remaining = pendingCounts.get(key) ?? 0;
    if (remaining <= 0) {
      continue;
    }
    pendingCounts.set(key, remaining - 1);
    filtered.push(structuredClone(item));
  }
  if (filtered.length > 0) {
    return filtered;
  }
  return existingSnapshot === undefined ? [] : existingSnapshot;
}

function stripTransientCallIds(value: AgentInputItem): AgentInputItem;
function stripTransientCallIds(value: unknown): unknown;
function stripTransientCallIds(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => stripTransientCallIds(entry));
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const record = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  const isProtocolItem =
    typeof record.type === 'string' && record.type.length > 0;
  const shouldStripId = isProtocolItem && shouldStripIdForProtocolItem(record);
  for (const [key, entry] of Object.entries(record)) {
    if (shouldStripId && key === 'id') {
      continue;
    }
    result[key] = stripTransientCallIds(entry);
  }
  return result;
}

function buildSourceOccurrenceCounts(
  sourceItems: (AgentInputItem | undefined)[],
) {
  const sourceOccurrenceCounts = new WeakMap<AgentInputItem, number>();
  for (const source of sourceItems) {
    if (!source || typeof source !== 'object') {
      continue;
    }
    const nextCount = (sourceOccurrenceCounts.get(source) ?? 0) + 1;
    sourceOccurrenceCounts.set(source, nextCount);
  }
  return sourceOccurrenceCounts;
}

function shouldStripIdForProtocolItem(
  record: Record<string, unknown>,
): boolean {
  switch (record.type) {
    case 'tool_search_call':
    case 'tool_search_output':
      return hasToolSearchCallId(record);
    default:
      return false;
  }
}

function hasToolSearchCallId(record: Record<string, unknown>): boolean {
  const topLevelCallId = record.call_id ?? record.callId;
  if (typeof topLevelCallId === 'string' && topLevelCallId.length > 0) {
    return true;
  }

  const providerData = isPlainObject(record.providerData)
    ? (record.providerData as Record<string, unknown>)
    : undefined;
  const providerCallId = providerData?.call_id ?? providerData?.callId;
  return typeof providerCallId === 'string' && providerCallId.length > 0;
}

export async function saveStreamInputToSession(
  session: Session | undefined,
  sessionInputItems: AgentInputItem[] | undefined,
  options: {
    turnContext?: SessionTurnContext;
  } = {},
): Promise<void> {
  if (!session) {
    return;
  }
  if (!sessionInputItems || sessionInputItems.length === 0) {
    return;
  }
  const sanitizedInput = normalizeItemsForSessionPersistence(sessionInputItems);
  await session.addItems(sanitizedInput);
  await recordSessionInputTurn({
    session,
    turnContext: options.turnContext,
    userItems: sanitizedInput,
  });
}

export async function saveToSession(
  session: Session | undefined,
  sessionInputItems: AgentInputItem[] | undefined,
  result: RunResult<any, any>,
  options: {
    compactionStore?: CompactionStore;
    compactionSessionId?: string;
    turnContext?: SessionTurnContext;
  } = {},
): Promise<void> {
  const state = result.state;
  const alreadyPersisted = state._currentTurnPersistedItemCount ?? 0;
  const newRunItems = result.newItems.slice(alreadyPersisted);

  if (process.env.LYRA__DEBUG_SAVE_SESSION) {
    console.debug(
      'saveToSession:newRunItems',
      newRunItems.map((item) => item.type),
    );
  }

  await persistRunItemsToSession({
    session,
    state,
    newRunItems,
    extraInputItems: sessionInputItems,
    alreadyPersistedCount: alreadyPersisted,
    compactionStore: options.compactionStore,
    compactionSessionId: options.compactionSessionId,
    turnContext: options.turnContext,
  });
}

async function persistRunItemsToSession(options: {
  session?: Session;
  state: RunState<any, any>;
  newRunItems: RunItem[];
  extraInputItems?: AgentInputItem[] | undefined;
  alreadyPersistedCount: number;
  compactionStore?: CompactionStore;
  compactionSessionId?: string;
  turnContext?: SessionTurnContext;
}): Promise<void> {
  const {
    session,
    state,
    newRunItems,
    extraInputItems = [],
    alreadyPersistedCount,
    compactionStore,
    compactionSessionId,
    turnContext,
  } = options;

  if (!session) {
    return;
  }

  const itemsToSave = [
    ...extraInputItems,
    ...extractPersistableOutputItemsFromRunItems(newRunItems),
  ];
  const outputItemsToSave =
    extractPersistableOutputItemsFromRunItems(newRunItems);

  if (itemsToSave.length === 0) {
    await persistCompactionRecords({
      store: compactionStore,
      sessionId: compactionSessionId,
      runItems: newRunItems,
    });
    await recordSessionFullTurn({
      session,
      turnContext,
      userItems: normalizeItemsForSessionPersistence(extraInputItems),
      assistantItems: [],
      assistantStatus: getAssistantTurnStatus(state),
    });
    state._currentTurnPersistedItemCount =
      alreadyPersistedCount + newRunItems.length;
    return;
  }

  const sanitizedItems = normalizeItemsForSessionPersistence(itemsToSave);
  await session.addItems(sanitizedItems);
  await recordSessionFullTurn({
    session,
    turnContext,
    userItems: normalizeItemsForSessionPersistence(extraInputItems),
    assistantItems: normalizeItemsForSessionPersistence(outputItemsToSave),
    assistantStatus: getAssistantTurnStatus(state),
  });
  await persistCompactionRecords({
    store: compactionStore,
    sessionId: compactionSessionId,
    runItems: newRunItems,
  });
  state._currentTurnPersistedItemCount =
    alreadyPersistedCount + newRunItems.length;
}

export async function saveStreamResultToSession(
  session: Session | undefined,
  result: StreamedRunResult<any, any>,
  options: {
    compactionStore?: CompactionStore;
    compactionSessionId?: string;
    turnContext?: SessionTurnContext;
  } = {},
): Promise<void> {
  const state = result.state;
  const alreadyPersisted = state._currentTurnPersistedItemCount ?? 0;
  const newRunItems = result.newItems.slice(alreadyPersisted);

  await persistRunItemsToSession({
    session,
    state,
    newRunItems,
    alreadyPersistedCount: alreadyPersisted,
    compactionStore: options.compactionStore,
    compactionSessionId: options.compactionSessionId,
    turnContext: options.turnContext,
  });
}

async function persistCompactionRecords(options: {
  store?: CompactionStore;
  sessionId?: string;
  runItems: RunItem[];
}): Promise<void> {
  const { store, sessionId, runItems } = options;
  if (!store || !sessionId) {
    return;
  }
  const records = runItems.flatMap((item) => {
    const record = getCompactionRecordFromRunItem(item);
    return record ? [record] : [];
  });
  if (records.length === 0) {
    return;
  }
  await store.appendRecords(sessionId, records);
}

export type PreparedInputWithSessionResult = {
  preparedInput: string | AgentInputItem[];
  sessionItems?: AgentInputItem[];
};
export async function prepareInputItemsWithSession(
  input: string | AgentInputItem[],
  session?: Session,
  sessionInputCallback?: SessionInputCallback,
  options?: {
    includeHistoryInPreparedInput?: boolean;
    preserveDroppedNewItems?: boolean;
    reasoningItemIdPolicy?: ReasoningItemIdPolicy;
  },
): Promise<PreparedInputWithSessionResult> {
  if (!session) {
    return {
      preparedInput: input,
      sessionItems: undefined,
    };
  }

  const includeHistoryInPreparedInput =
    options?.includeHistoryInPreparedInput ?? true;
  const preserveDroppedNewItems = options?.preserveDroppedNewItems ?? false;

  const history = await session.getItems();
  const historyIds = collectAgentInputItemIds(history);
  const newInputItems = ensureAgentInputItemIds(toAgentInputList(input), {
    usedIds: historyIds,
  });

  if (!sessionInputCallback) {
    const historyForModelInput = history.map((item) =>
      prepareHistoryItemForModelInput(session, item, {
        reasoningItemIdPolicy: options?.reasoningItemIdPolicy,
      }),
    );
    const preparedInput = includeHistoryInPreparedInput
      ? dropOrphanToolCalls([...historyForModelInput, ...newInputItems], {
          pruningIndexes: new Set(history.map((_, index) => index)),
        })
      : newInputItems;
    return {
      preparedInput,
      sessionItems: newInputItems,
    };
  }

  const historySnapshot = history.slice();
  const newInputSnapshot = newInputItems.slice();

  const combined = await sessionInputCallback(history, newInputItems);
  if (!Array.isArray(combined)) {
    throw new UserError(
      'Session input callback must return an array of AgentInputItem objects.',
    );
  }

  const historyCounts = buildItemFrequencyMap(historySnapshot, {
    session,
    prepareForModelInput: true,
  });
  const newInputCounts = buildItemFrequencyMap(newInputSnapshot);
  const historyRefs = buildAgentInputPool(historySnapshot);
  const newInputRefs = buildAgentInputPool(newInputSnapshot);
  const historyIndexes = new Set<number>();

  const appended: AgentInputItem[] = [];
  for (const [index, item] of combined.entries()) {
    const historyKey = getHistoryItemModelInputKey(session, item);
    const newInputKey = getAgentInputItemKey(item);
    if (removeAgentInputFromPool(newInputRefs, item)) {
      decrementCount(newInputCounts, newInputKey);
      appended.push(item);
      continue;
    }

    if (removeAgentInputFromPool(historyRefs, item)) {
      decrementCount(historyCounts, historyKey);
      historyIndexes.add(index);
      continue;
    }

    const historyRemaining = historyCounts.get(historyKey) ?? 0;
    if (historyRemaining > 0) {
      historyCounts.set(historyKey, historyRemaining - 1);
      historyIndexes.add(index);
      continue;
    }

    const newRemaining = newInputCounts.get(newInputKey) ?? 0;
    if (newRemaining > 0) {
      newInputCounts.set(newInputKey, newRemaining - 1);
      appended.push(item);
      continue;
    }

    appended.push(item);
  }

  const preparedItems = includeHistoryInPreparedInput
    ? combined
    : appended.length > 0
      ? appended
      : preserveDroppedNewItems
        ? newInputSnapshot
        : [];

  if (
    preserveDroppedNewItems &&
    appended.length === 0 &&
    newInputSnapshot.length > 0
  ) {
    // In server-managed conversations we cannot drop the turn delta; restore it and warn callers.
    logger.warn(
      'sessionInputCallback dropped all new inputs in a server-managed conversation; original turn inputs were restored to avoid losing the API delta. Keep at least one new item or omit conversationId if you intended to drop them.',
    );
  }

  const prunedPreparedItems = includeHistoryInPreparedInput
    ? dropOrphanToolCalls(
        prepareHistoryItemsForModelInput(
          session,
          preparedItems,
          historyIndexes,
          {
            reasoningItemIdPolicy: options?.reasoningItemIdPolicy,
          },
        ),
        { pruningIndexes: historyIndexes },
      )
    : preparedItems;

  return {
    preparedInput: prunedPreparedItems,
    sessionItems: appended,
  };
}

/**
 * 为 local compaction 路径准备模型输入。
 *
 * session 仍然保存完整的原始对话历史，但模型只应该看到组装后的压缩视图：
 * 保留的 system message、当前生效的 summary，以及未被压缩的尾部历史。
 * 本轮新输入会追加到这个模型视图后面；而 `sessionItems` 只保留需要写回
 * 原始 session transcript 的本轮新增项。
 */
export async function prepareInputItemsWithSessionAndCompaction(
  input: string | AgentInputItem[],
  session: Session | undefined,
  compactionRecords: CompactionRecord[],
  sessionInputCallback?: SessionInputCallback,
  options?: {
    reasoningItemIdPolicy?: ReasoningItemIdPolicy;
  },
): Promise<PreparedInputWithSessionResult> {
  if (!session) {
    return {
      preparedInput: input,
      sessionItems: undefined,
    };
  }

  const history = await session.getItems();
  const historyIds = collectAgentInputItemIds(history);
  const newInputItems = ensureAgentInputItemIds(toAgentInputList(input), {
    usedIds: historyIds,
  });
  const historyForModelInput = history.map((item) =>
    prepareHistoryItemForModelInput(session, item, {
      reasoningItemIdPolicy: options?.reasoningItemIdPolicy,
    }),
  );
  const modelContextHistory = assembleModelContext(
    historyForModelInput,
    compactionRecords,
  );

  if (!sessionInputCallback) {
    return {
      preparedInput: dropOrphanToolCalls(
        [...modelContextHistory, ...newInputItems],
        {
          pruningIndexes: new Set(modelContextHistory.map((_, index) => index)),
        },
      ),
      sessionItems: newInputItems,
    };
  }

  const combined = await sessionInputCallback(
    modelContextHistory,
    newInputItems,
  );
  if (!Array.isArray(combined)) {
    throw new UserError(
      'Session input callback must return an array of AgentInputItem objects.',
    );
  }

  return {
    preparedInput: dropOrphanToolCalls(combined),
    sessionItems: getNewItemsForCompactionSessionPersistence(
      combined,
      newInputItems,
    ),
  };
}

function getNewItemsForCompactionSessionPersistence(
  combined: AgentInputItem[],
  newInputItems: AgentInputItem[],
): AgentInputItem[] {
  const newInputRefs = buildAgentInputPool(newInputItems);
  const appended: AgentInputItem[] = [];
  for (const item of combined) {
    if (removeAgentInputFromPool(newInputRefs, item)) {
      appended.push(item);
    }
  }
  return appended.length > 0 ? appended : newInputItems;
}

function prepareHistoryItemsForModelInput(
  session: Session,
  items: AgentInputItem[],
  historyIndexes: Set<number>,
  options?: { reasoningItemIdPolicy?: ReasoningItemIdPolicy },
): AgentInputItem[] {
  if (
    !session.prepareHistoryItemForModelInput &&
    !options?.reasoningItemIdPolicy
  ) {
    return items;
  }
  return items.map((item, index) =>
    historyIndexes.has(index)
      ? prepareHistoryItemForModelInput(session, item, options)
      : item,
  );
}

function prepareHistoryItemForModelInput(
  session: Session,
  item: AgentInputItem,
  options?: { reasoningItemIdPolicy?: ReasoningItemIdPolicy },
): AgentInputItem {
  const prepared = session.prepareHistoryItemForModelInput?.(item) ?? item;
  if (options?.reasoningItemIdPolicy !== 'omit') {
    return prepared;
  }
  if (
    !prepared ||
    typeof prepared !== 'object' ||
    prepared.type !== 'reasoning'
  ) {
    return prepared;
  }
  const { id: _id, ...withoutId } = prepared as Record<string, unknown>;
  return withoutId as AgentInputItem;
}

function buildItemFrequencyMap(
  items: AgentInputItem[],
  options?: { session?: Session; prepareForModelInput?: boolean },
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key =
      options?.prepareForModelInput && options.session
        ? getHistoryItemModelInputKey(options.session, item)
        : getAgentInputItemKey(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function getHistoryItemModelInputKey(
  session: Session,
  item: AgentInputItem,
): string {
  return getAgentInputItemKey(prepareHistoryItemForModelInput(session, item));
}

function decrementCount(map: Map<string, number>, key: string) {
  const remaining = (map.get(key) ?? 0) - 1;
  if (remaining <= 0) {
    map.delete(key);
  } else {
    map.set(key, remaining);
  }
}
